import {
    type AgeCategory,
    AgeCategoryType,
} from "crqlar-react-common/dist/models/ageCategory/ageCategory.model";
import {Fragment} from "react";
import {
    AgeCategoryElement,
    type AgeCategoryElementVariant,
} from "./AgeCategoryElement";
import {ElementChipList} from "./base/ElementChipList";
import {
    aggregateAgeCategoryCounts,
    hasConfiguredAgeCategoryAliases,
    type AgeCategoryCountInput,
    type ResolvedAgeCategoryCount,
    resolveAgeCategoryCounts,
    resolveAgeCategoryForBirthday,
} from "../ageCategoryDefaults";
import Box from "@mui/material/Box";
import type {
    ReservationListAliasAgeCategoriesDTO,
} from "crqlar-react-common/dist/models/aggregation/reservationListAggregation.model";
import {AliasAgeCategoriesElement} from "./AliasAgeCategoriesElement";

interface AgeCategoriesElementProps {
    ageCategories: AgeCategory[];
    ageCategoryCounts?: AgeCategoryCountInput[];
    aliasAgeCategories?: ReservationListAliasAgeCategoriesDTO;
    birthdays?: string[];
    referenceDate?: Date;
    computedAgeCategories?: {
        adults?: number;
        teenagers?: number;
        children?: number;
        infants?: number;
    };
    totalGuests?: number;
    asPrint?: boolean;
    variant?: AgeCategoryElementVariant;
}

export function AgeCategoriesElement({
    ageCategories,
    ageCategoryCounts = [],
    aliasAgeCategories,
    birthdays = [],
    referenceDate,
    computedAgeCategories,
    totalGuests = 0,
    asPrint = false,
    variant = "chip",
}: AgeCategoriesElementProps) {
    if (
        hasConfiguredAgeCategoryAliases(ageCategories)
        && aliasAgeCategories?.some(({label, count}) => label.trim().length > 0 && count > 0)
    ) {
        return (
            <AliasAgeCategoriesElement
                aliasAgeCategories={aliasAgeCategories}
                ageCategories={ageCategories}
                asPrint={asPrint}
                variant={variant}
            />
        );
    }

    const countedCategories = resolveAgeCategoryCounts(ageCategories, ageCategoryCounts);
    const hasResolvedAllCategoryCounts = Boolean(countedCategories?.length);
    const computedGuestCount = (computedAgeCategories?.adults ?? 0)
        + (computedAgeCategories?.teenagers ?? 0)
        + (computedAgeCategories?.children ?? 0)
        + (computedAgeCategories?.infants ?? 0);
    const resolvedCategories = referenceDate && birthdays.length === computedGuestCount
        ? birthdays.map(birthday => (
            resolveAgeCategoryForBirthday(ageCategories, birthday, referenceDate)
        ))
        : [];
    const hasResolvedAllCategories = computedGuestCount > 0
        && resolvedCategories.length === computedGuestCount
        && resolvedCategories.every(Boolean);
    const birthdayCategories = resolvedCategories.flatMap(ageCategory => (
        ageCategory ? [{...ageCategory, count: 1}] : []
    ));
    let categories: ResolvedAgeCategoryCount[];
    if (hasResolvedAllCategoryCounts) {
        categories = countedCategories ?? [];
    } else if (hasResolvedAllCategories) {
        categories = aggregateAgeCategoryCounts(birthdayCategories);
    } else {
        categories = [
            {
                type: AgeCategoryType.ADULT,
                count: computedAgeCategories?.adults ?? 0,
            },
            {
                type: AgeCategoryType.TEENAGER,
                count: computedAgeCategories?.teenagers ?? 0,
            },
            {
                type: AgeCategoryType.CHILD,
                count: computedAgeCategories?.children ?? 0,
            },
            {
                type: AgeCategoryType.INFANT,
                count: computedAgeCategories?.infants ?? 0,
            },
        ];
    }
    const visibleCategories = categories.filter(ageCategory => ageCategory.count > 0);
    if (!visibleCategories.length && totalGuests > 0) {
        visibleCategories.push({count: totalGuests});
    }

    if (!visibleCategories.length) return null;

    if (asPrint) {
        return <>{visibleCategories.map((ageCategory, index) => (
            <Fragment key={ageCategory.id ?? `${ageCategory.type}-${ageCategory.name}-${index}`}>
                {index > 0 && ", "}
                <AgeCategoryElement
                    ageCategory={ageCategory}
                    count={ageCategory.count}
                    asPrint
                />
            </Fragment>
        ))}</>;
    }

    if (variant === "inline") {
        return (
            <Box sx={{display: "flex", alignItems: "center", gap: 0.75, flex: "0 0 auto"}}>
                {visibleCategories.map((ageCategory, index) => (
                    <AgeCategoryElement
                        key={ageCategory.id ?? `${ageCategory.type}-${ageCategory.name}-${index}`}
                        ageCategory={ageCategory}
                        count={ageCategory.count}
                        variant="inline"
                    />
                ))}
            </Box>
        );
    }

    return (
        <ElementChipList>
            {visibleCategories.map((ageCategory, index) => (
                <AgeCategoryElement
                    key={ageCategory.id ?? `${ageCategory.type}-${ageCategory.name}-${index}`}
                    ageCategory={ageCategory}
                    count={ageCategory.count}
                />
            ))}
        </ElementChipList>
    );
}
